import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  CircularProgress,
  Stack,
} from "@mui/material";
import { TUseXElement } from "../../../hooks/useXElement";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import {
  IXGUIFieldBase,
  TOnChange,
  TXGUIField,
} from "../../../utils/interfaces/gui/fields.interfaces";
import { IDataFetching } from "../../../hooks/useXoraia";
import { useCategories } from "../../../hooks/useCategories";

const XGUITextField = React.lazy(() => import("../components/XGUITextField"));

const XGUIFilePicker = React.lazy(() => import("../components/XGUIFilePicker"));

const XGUISelectOptionField = React.lazy(
  () => import("../components/XGUISelectOptionField")
);

const XGUIToggleOptionField = React.lazy(
  () => import("../components/XGUIToggleOptionField")
);

const XGUIDataTab: React.FC<{
  selectedElement: TUseXElement<any>;
  setSelectedElement: any;
}> = ({ selectedElement, setSelectedElement }) => {
  const { categories } = useCategories();
  // WE TRACK THE BREAK POINTS CHANGES
  // TO UPDATE THE ELEMENT RELATIVELY TO THE BREAKPOINT
  const breakpoint = useBreakpoint();

  const data: IDataFetching = selectedElement.dataFetching ?? {};

  if (!breakpoint) return null;

  // CHANGE EVENT FOR ALL COMPONENTS
  const onChange: TOnChange<any> = (value, field) => {
    setSelectedElement((old: any) => {
      const dataFetching = { ...old.dataFetching, [field]: value };

      Object.keys(dataFetching).forEach((key) => {
        if (
          typeof dataFetching[key] === "undefined" ||
          dataFetching[key] === null
        ) {
          delete dataFetching[key];
        }
      });

      const payload = {
        ...old,
        dataFetching,
      };

      return payload;
    });
  };

  console.log("categories =>", categories);

  const sections: TXGUIField[] = [
    {
      label: "Actif",
      name: "enabled",
      type: "toggle_option",
      options: [
        { value: false, label: "Non" },
        { value: true, label: "Oui" },
      ],
      defaultValue: data.enabled ?? false,
    },
    {
      label: "Max. Par Page",
      name: "limit",
      type: "number",
      placeholder: "8",
      defaultValue: data.limit,
    },
    {
      label: "Ordonner",
      name: "order",
      type: "select_option",
      options: [
        { value: "name", label: "Par Nom" },
        { value: "price", label: "Par Prix" },
        { value: "rating", label: "Par Notes" },
        { value: "qty", label: "Par Quantité" },
        { value: "createdAt", label: "Par Date" },
      ],
      defaultValue: data.order ?? "createdAt",
    },
    {
      label: "Sens",
      name: "sort",
      type: "select_option",
      options: [
        { value: "asc", label: "Croissant" },
        { value: "desc", label: "Decroissant" },
      ],
      defaultValue: data.sort ?? "desc",
    },
  ];

  if (categories.length > 0) {
    sections.unshift({
      label: "Categories",
      name: "categoryId",
      type: "select_option",
      options: categories.map((cat) => ({ label: cat.name, value: cat.id })),
    });
  }

  return (
    <Stack
      sx={{
        ".MuiAccordion-root": {
          borderRadius: 0,
          color: "white",
          bgcolor: "#0E0D18",

          "&.MuiPaper-elevation": {
            boxShadow: "none",
          },

          ".MuiSvgIcon-root": {
            fontSize: 15,
            color: "white",
          },

          ".MuiAccordionSummary-root": {
            px: 0,
          },
        },
        ".MuiToggleButtonGroup-root, .MuiInputBase-root, .MuiFab-root": {
          border: "2px solid #222132",
          minHeight: 30,
        },
        ".MuiToggleButtonGroup-root": {
          bgcolor: "#222132",

          ".MuiToggleButton-root": {
            py: 0,
            fontSize: 9.5,
            color: "white",
            fontWeight: 700,

            "&.Mui-selected": {
              bgcolor: "rgba(0, 0, 0, 0.5)",
              borderRadius: 1,
            },

            "&.Mui-disabled": {
              color: "rgba(0, 0, 0, 0.38)",
              svg: {
                color: "inherit",
              },
            },
          },
        },
        ".MuiButtonBase-root": {
          bgcolor: "transparent",
          minHeight: 0,

          ".MuiAccordionSummary-content": {
            my: 1,

            ".MuiTypography-body1": {
              fontSize: 12,
            },
          },
        },
        ".MuiAccordionDetails-root": {
          pt: 0,
          pb: 1,
          px: 0,
          color: "#9494AE",
          bgcolor: "transparent",
          fontSize: 9,

          "& .MuiTypography-root": {
            letterSpacing: 0.5,
            fontWeight: 700,
            textTransform: "uppercase",
            fontSize: "inherit",
          },

          "& .MuiSelect-select": {
            py: 0.5,
            px: 1,
          },

          "& .Side": {
            width: "100%",
            maxWidth: 170,
            fontSize: 9,
          },

          "& .MuiInputBase-root": {
            color: "white",
            bgcolor: "#222132",
            fontSize: 12,
            overflow: "hidden",
            borderRadius: 1,

            "&.MuiInputBase-multiline": {
              p: 0,
            },

            "input, & .MuiInputBase-inputMultiline, & .MuiInputBase-input": {
              p: 0,
              pl: 1,
              border: 0,
            },

            ".MuiButtonBase-root": {
              minWidth: 50,
              borderRadius: 0.5,

              // p: 0,
              bgcolor: "rgba(0,0,0,0.5)",
              fontSize: 10,
              lineHeight: "1.2em",
              height: 25,

              ".MuiOutlinedInput-notchedOutline": {
                border: 0,
              },

              ".MuiSelect-select": {
                p: 0.25,
                textAlign: "center",
              },

              ".MuiSelect-icon": {
                display: "none",
              },
            },
          },
          ".MuiInputBase-adornedEnd": {
            pr: 0,
            // py: 0.5,
          },
        },
      }}
    >
      <Accordion expanded disableGutters sx={{ pt: 2 }}>
        <AccordionDetails>
          <React.Suspense fallback={<CircularProgress />}>
            {sections.map((field) =>
              generateXGUIField({
                field,
                onChange,
                currentValue: data[field.name],
                index: selectedElement.index ?? "",
                visible: true,
              })
            )}
          </React.Suspense>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

export default XGUIDataTab;

const generateXGUIField = (args: IXGUIFieldBase) => {
  if (!args.visible) return null;

  const key = args.index + args.field.name;

  switch (args.field.type) {
    case "text":
    case "number":
      return (
        <React.Suspense key={key}>
          <XGUITextField {...args} type={args.field.type} />
        </React.Suspense>
      );

    case "select_option":
      return (
        <React.Suspense key={key}>
          <XGUISelectOptionField {...args} options={args.field.options ?? []} />
        </React.Suspense>
      );
    case "toggle_option":
      return (
        <React.Suspense key={key}>
          <XGUIToggleOptionField {...args} options={args.field.options ?? []} />
        </React.Suspense>
      );

    case "image":
      return (
        <React.Suspense key={key}>
          <XGUIFilePicker {...args} type="image" />
        </React.Suspense>
      );
    default:
      return null;
  }
};
